import React, { useEffect, useRef, useState } from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import cn from 'classnames';

import Portal from '@/components/common/Portal/Portal';
import ErrorTooltip from './ErrorTooltip';
import styles from './cell-renderer.module.scss';

interface IAgGridCellRendererProps extends ICellRendererParams {
  errors: string[],
}

export default function AgGridCellRenderer({
  valueFormatted,
  value,
  colDef,
  errors = [],
}: IAgGridCellRendererProps) {
  const textToRenderInsideCell = valueFormatted ?? value;
  const [isVisibleError, setIsVisibleError] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const cellRef = useRef<HTMLDivElement>(null);

  const onMouseEnter = (event: MouseEvent) => {
    if (!event.target) return;
    const { top, height, left } = (event.target as HTMLDivElement).getBoundingClientRect();

    setIsVisibleError(true);
    setPosition({ top: top + height, left });
  };

  const onMouseLeave = () => {
    setIsVisibleError(false);
  };

  useEffect(() => {
    const element = cellRef.current;
    if (!element) return;

    element.addEventListener('mouseenter', onMouseEnter);
    element.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('scroll', onMouseLeave);

    return () => {
      element.removeEventListener('mouseenter', onMouseEnter);
      element.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onMouseLeave);
    };
  }, []);

  return (
    <div
      ref={cellRef}
      className={errors.length > 0 ? cn(styles['cell-renderer'], styles.invalid) : undefined} >
      {textToRenderInsideCell}

      {isVisibleError && errors.length > 0 && (
        <Portal>
          <div
            className={styles['error-wrapper']}
            style={{ top: position.top, left: position.left }}
          >
            <ErrorTooltip
              title={colDef?.headerName}
              errors={errors}
            />
          </div>
        </Portal>
      )}
    </div>
  );
}
